// store/admin/certificates/store/_reducer.js
import * as types from './_actionTypes';

const initialState = {
  // Data
  certificates: [],
  selectedCertificate: null,
  pendingApprovals: [],
  stats: {
    total: 0,
    approved: 0,
    pending: 0,
    revoked: 0,
    approvalRate: 0
  },
  colleges: [],
  courses: [],
  verificationResult: null,
  bulkApproveResult: null,
  issueManualResult: null,

  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },
  pendingPagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },

  // UI State
  selectedCollegeId: null,
  selectedCourseId: null,

  // Loading states
  loading: false,
  certificateLoading: false,
  pendingLoading: false,
  statsLoading: false,
  approveLoading: false,
  issueManualLoading: false,
  bulkApproveLoading: false,
  revokeLoading: false,
  verifyLoading: false,
  collegesLoading: false,
  coursesLoading: false,

  // Error states
  error: null,
  approveError: null,
  bulkApproveError: null,
  revokeError: null,
  verifyError: null
};

const adminCertificatesReducer = (state = initialState, action) => {
  switch (action.type) {
    // =====================================================
    // CERTIFICATES LIST
    // =====================================================
    case types.FETCH_CERTIFICATES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };

    case types.FETCH_CERTIFICATES_SUCCESS:
      return {
        ...state,
        loading: false,
        certificates: action.payload.certificates,
        pagination: action.payload.pagination,
        error: null
      };

    case types.FETCH_CERTIFICATES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // =====================================================
    // CERTIFICATE BY ID
    // =====================================================
    case types.FETCH_CERTIFICATE_BY_ID_REQUEST:
      return {
        ...state,
        certificateLoading: true,
        selectedCertificate: null,
        error: null
      };

    case types.FETCH_CERTIFICATE_BY_ID_SUCCESS:
      return {
        ...state,
        certificateLoading: false,
        selectedCertificate: action.payload,
        error: null
      };

    case types.FETCH_CERTIFICATE_BY_ID_FAILURE:
      return {
        ...state,
        certificateLoading: false,
        error: action.payload
      };

    // =====================================================
    // PENDING APPROVALS
    // =====================================================
    case types.FETCH_PENDING_APPROVALS_REQUEST:
      return {
        ...state,
        pendingLoading: true,
        error: null
      };

    case types.FETCH_PENDING_APPROVALS_SUCCESS:
      return {
        ...state,
        pendingLoading: false,
        pendingApprovals: action.payload.pendingApprovals,
        pendingPagination: action.payload.pagination,
        error: null
      };

    case types.FETCH_PENDING_APPROVALS_FAILURE:
      return {
        ...state,
        pendingLoading: false,
        error: action.payload
      };

    // =====================================================
    // CERTIFICATE STATS
    // =====================================================
    case types.FETCH_CERTIFICATE_STATS_REQUEST:
      return {
        ...state,
        statsLoading: true
      };

    case types.FETCH_CERTIFICATE_STATS_SUCCESS:
      return {
        ...state,
        statsLoading: false,
        stats: action.payload
      };

    case types.FETCH_CERTIFICATE_STATS_FAILURE:
      return {
        ...state,
        statsLoading: false,
        error: action.payload
      };

    // =====================================================
    // APPROVE CERTIFICATE (Single)
    // =====================================================
    case types.APPROVE_CERTIFICATE_REQUEST:
      return {
        ...state,
        approveLoading: true,
        approveError: null
      };

    case types.APPROVE_CERTIFICATE_SUCCESS:
      return {
        ...state,
        approveLoading: false,
        // Add to certificates list
        certificates: [action.payload, ...state.certificates],
        // Remove from pending if exists
        pendingApprovals: state.pendingApprovals.filter(
          item => item.student.id !== action.payload.student_id
        ),
        approveError: null
      };

    case types.APPROVE_CERTIFICATE_FAILURE:
      return {
        ...state,
        approveLoading: false,
        approveError: action.payload
      };

    // =====================================================
    // ISSUE MANUAL CERTIFICATE
    // =====================================================
    case types.ISSUE_MANUAL_CERTIFICATE_REQUEST:
      return {
        ...state,
        issueManualLoading: true,
        issueManualResult: null,
        error: null
      };

    case types.ISSUE_MANUAL_CERTIFICATE_SUCCESS:
      return {
        ...state,
        issueManualLoading: false,
        issueManualResult: action.payload,
        // Surface the new manual cert in the list if one is loaded
        certificates: action.payload.alreadyExists
          ? state.certificates
          : [action.payload.certificate, ...state.certificates],
        error: null
      };

    case types.ISSUE_MANUAL_CERTIFICATE_FAILURE:
      return {
        ...state,
        issueManualLoading: false,
        issueManualResult: { error: action.payload }
      };

    case types.CLEAR_ISSUE_MANUAL_RESULT:
      return {
        ...state,
        issueManualResult: null
      };

    // =====================================================
    // BULK APPROVE CERTIFICATES
    // =====================================================
    case types.BULK_APPROVE_CERTIFICATES_REQUEST:
      return {
        ...state,
        bulkApproveLoading: true,
        bulkApproveResult: null,
        bulkApproveError: null
      };

    case types.BULK_APPROVE_CERTIFICATES_SUCCESS:
      return {
        ...state,
        bulkApproveLoading: false,
        bulkApproveResult: action.payload,
        // Clear pending approvals since they've been processed
        pendingApprovals: [],
        bulkApproveError: null
      };

    case types.BULK_APPROVE_CERTIFICATES_FAILURE:
      return {
        ...state,
        bulkApproveLoading: false,
        bulkApproveError: action.payload
      };

    // =====================================================
    // REVOKE CERTIFICATE
    // =====================================================
    case types.REVOKE_CERTIFICATE_REQUEST:
      return {
        ...state,
        revokeLoading: true,
        revokeError: null
      };

    case types.REVOKE_CERTIFICATE_SUCCESS:
      return {
        ...state,
        revokeLoading: false,
        // Update certificate in list
        certificates: state.certificates.map(cert =>
          cert.id === action.payload.id
            ? { ...cert, is_revoked: true, ...action.payload }
            : cert
        ),
        // Update selected certificate if it's the one being revoked
        selectedCertificate: state.selectedCertificate?.id === action.payload.id
          ? { ...state.selectedCertificate, is_revoked: true, ...action.payload }
          : state.selectedCertificate,
        revokeError: null
      };

    case types.REVOKE_CERTIFICATE_FAILURE:
      return {
        ...state,
        revokeLoading: false,
        revokeError: action.payload
      };

    // =====================================================
    // VERIFY CERTIFICATE
    // =====================================================
    case types.VERIFY_CERTIFICATE_REQUEST:
      return {
        ...state,
        verifyLoading: true,
        verificationResult: null,
        verifyError: null
      };

    case types.VERIFY_CERTIFICATE_SUCCESS:
      return {
        ...state,
        verifyLoading: false,
        verificationResult: action.payload,
        verifyError: null
      };

    case types.VERIFY_CERTIFICATE_FAILURE:
      return {
        ...state,
        verifyLoading: false,
        verifyError: action.payload
      };

    // =====================================================
    // COLLEGES
    // =====================================================
    case types.FETCH_COLLEGES_REQUEST:
      return {
        ...state,
        collegesLoading: true
      };

    case types.FETCH_COLLEGES_SUCCESS:
      return {
        ...state,
        collegesLoading: false,
        colleges: action.payload
      };

    case types.FETCH_COLLEGES_FAILURE:
      return {
        ...state,
        collegesLoading: false,
        error: action.payload
      };

    // =====================================================
    // COURSES
    // =====================================================
    case types.FETCH_COURSES_REQUEST:
      return {
        ...state,
        coursesLoading: true
      };

    case types.FETCH_COURSES_SUCCESS:
      return {
        ...state,
        coursesLoading: false,
        courses: action.payload
      };

    case types.FETCH_COURSES_FAILURE:
      return {
        ...state,
        coursesLoading: false,
        error: action.payload
      };

    // =====================================================
    // UI STATE
    // =====================================================
    case types.SET_SELECTED_COLLEGE:
      return {
        ...state,
        selectedCollegeId: action.payload,
        pendingApprovals: [] // Clear pending when college changes
      };

    case types.SET_SELECTED_COURSE:
      return {
        ...state,
        selectedCourseId: action.payload,
        pendingApprovals: [] // Clear pending when course changes
      };

    case types.CLEAR_CERTIFICATE_ERROR:
      return {
        ...state,
        error: null,
        approveError: null,
        bulkApproveError: null,
        revokeError: null,
        verifyError: null
      };

    case types.RESET_CERTIFICATE_STATE:
      return initialState;

    default:
      return state;
  }
};

export default adminCertificatesReducer;